import React from "react";
import { NavLink } from "react-router-dom";
import { LogOut, ArrowLeftToLine, ArrowRightToLine } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import sidebarLogo from "../assets/sidebar-logo.svg";
import { SIDEBAR_NAV_ITEMS } from "../app/routesConfig";

const NAV_ICON_SIZE = 25; // 1.5x larger than previous base size
const ACTION_ICON_SIZE = 25;
const COMPACT_CONTROL_ICON_SIZE = 20;

export const Sidebar = ({ mode = "expanded", onModeChange }) => {
  const { logout } = useAuth();

  const isExpanded = mode === "expanded";
  const isCompact = mode === "compact";
  const nextMode = isExpanded ? "compact" : "expanded";

  const handleCycleMode = () => {
    if (typeof onModeChange === "function") {
      onModeChange(nextMode);
    }
  };

  const sidebarWidth = isExpanded ? "11rem" : "4.5rem";
  const navSpacing = isCompact ? "space-y-1" : "space-y-1.5";
  const itemAlignment = isCompact ? "justify-center" : "gap-3";
  const toggleLabel = isExpanded ? "Collapse sidebar" : "Expand sidebar";
  const headerLayoutClass = isExpanded
    ? "justify-between gap-2 px-1"
    : "justify-center gap-0 px-1";
  const toggleSizeClass = isExpanded ? "h-7 w-7 rounded-lg" : "h-7 w-7 rounded-lg"; // smaller toggle button
  const toggleIconSize = isExpanded ? 14 : 14;

  return (
    <aside
      style={{ width: sidebarWidth }}
      className={`hidden md:flex md:fixed md:top-0 md:left-0 md:bottom-0 flex-shrink-0 flex-col bg-white/80 backdrop-blur-xl shadow-2xl border-r border-white/60 md:h-screen md:overflow-y-auto z-40 overflow-hidden transition-all duration-300 ease-in-out`}
    >
      <div className="border-b border-white/60 px-1 pt-2 pb-2">
        <div className={`flex items-center ${headerLayoutClass} rounded-2xl py-1`}>
          {isExpanded && (
            <img
              src={sidebarLogo}
              alt="Control Center logo"
              className="h-9 w-9 select-none rounded-2xl shadow-md"
              draggable="false"
            />
          )}
          <button
            type="button"
            onClick={handleCycleMode}
            aria-label={toggleLabel}
            className={`flex ${toggleSizeClass} items-center justify-center transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-white/60 ${
              isExpanded
                ? "bg-slate-900 text-white shadow-lg hover:-translate-x-1"
                : "bg-white/80 text-slate-600 shadow-inner hover:translate-x-1"
            }`}
          >
            {isExpanded ? (
              <ArrowLeftToLine size={toggleIconSize} strokeWidth={2.5} />
            ) : (
              <ArrowRightToLine size={toggleIconSize} strokeWidth={2.5} />
            )}
          </button>
        </div>
      </div>

      <nav className={`flex-1 overflow-y-auto px-2 py-2 ${navSpacing}`}>
        {SIDEBAR_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center ${itemAlignment} px-2.5 py-2.5 rounded-2xl text-sm font-medium transition ${
                  isActive
                    ? "bg-slate-900 text-white shadow-lg"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/60"
                }`
              }
              title={item.label}
            >
              {({ isActive }) => (
                <>
                  {item.useActiveState ? <Icon size={NAV_ICON_SIZE} isActive={isActive} /> : <Icon size={NAV_ICON_SIZE} />}
                  <span
                    className={`whitespace-nowrap overflow-hidden text-left text-sm transition-[max-width,opacity,transform] duration-200 ease-in-out ${
                      isExpanded ? "max-w-[8rem] opacity-100 translate-x-0" : "max-w-0 opacity-0 -translate-x-1"
                    }`}
                  >
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-white/60 px-2.5 pb-2.5 pt-2.5">
        {isExpanded ? (
            <button
              onClick={logout}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-gray-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Log out"
            >
              <LogOut size={COMPACT_CONTROL_ICON_SIZE} /> Log out
            </button>
        ) : (
          <div className="flex flex-col items-center">
            <button
              onClick={logout}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-200 text-slate-700 transition hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Log out"
            >
              <LogOut size={COMPACT_CONTROL_ICON_SIZE} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
